/**
 * CheckoutChamp Integration - Main JavaScript
 * This file handles all custom functionality for the subscription and checkout system
 */

(function() {
  'use strict';

  // Configuration globale
  const CONFIG = {
    apiBaseUrl: 'https://api.data.retroha.com',
    subscriptionVariantId: 56486961512821,
    subscriptionProductId: 15667842023797,
    campaignId: 9,
    shipProfileId: 7,
    get locale() {
      return document.documentElement.lang || 'fr';
    },
    shopDomain: null
  };

  // Utility Functions
  const Utils = {
    /**
     * Get current URL parameters
     */
    getUrlParams() {
      return new URLSearchParams(window.location.search);
    },

    /**
     * Show loading overlay
     */
    showLoading() {
      let overlay = document.getElementById('custom-loading-overlay');
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'custom-loading-overlay';
        overlay.className = 'loading-overlay';
        overlay.innerHTML = '<div class="loading-spinner"></div>';
        document.body.appendChild(overlay);
      }
      overlay.style.display = 'flex';
    },

    /**
     * Hide loading overlay
     */
    hideLoading() {
      const overlay = document.getElementById('custom-loading-overlay');
      if (overlay) {
        overlay.style.display = 'none';
      }
    },

    /**
     * Show error message
     */
    showError(message, containerId = null) {
      const errorDiv = document.createElement('div');
      errorDiv.className = 'error-message';
      errorDiv.textContent = message;
      
      if (containerId) {
        const container = document.getElementById(containerId);
        if (container) {
          container.insertBefore(errorDiv, container.firstChild);
          setTimeout(() => errorDiv.remove(), 5000);
        }
      } else {
        document.body.insertBefore(errorDiv, document.body.firstChild);
        setTimeout(() => errorDiv.remove(), 5000);
      }
    },

    /**
     * Show success message
     */
    showSuccess(message, containerId = null) {
      const successDiv = document.createElement('div');
      successDiv.className = 'success-message';
      successDiv.textContent = message;
      
      if (containerId) {
        const container = document.getElementById(containerId);
        if (container) {
          container.insertBefore(successDiv, container.firstChild);
          setTimeout(() => successDiv.remove(), 5000);
        }
      } else {
        document.body.insertBefore(successDiv, document.body.firstChild);
        setTimeout(() => successDiv.remove(), 5000);
      }
    },

    /**
     * Generate random footprint/ID
     */
    generateFootprint() {
      return 'fp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }
  };

  // Local Storage Manager for Customer Account
  const AccountStorage = {
    /**
     * Save customer data to localStorage
     */
    save(customerData) {
      try {
        localStorage.setItem('customer_account', JSON.stringify(customerData));
        localStorage.setItem('customer_logged_in', 'true');
      } catch (e) {
        console.error('Error saving customer data:', e);
      }
    },

    /**
     * Get customer data from localStorage
     */
    get() {
      try {
        const data = localStorage.getItem('customer_account');
        return data ? JSON.parse(data) : null;
      } catch (e) {
        console.error('Error retrieving customer data:', e);
        return null;
      }
    },

    /**
     * Check if customer is logged in
     */
    isLoggedIn() {
      return localStorage.getItem('customer_logged_in') === 'true';
    },

    /**
     * Clear customer data
     */
    clear() {
      localStorage.removeItem('customer_account');
      localStorage.removeItem('customer_logged_in');
    },

    /**
     * Check if customer has active subscription
     */
    hasActiveSubscription() {
      const data = this.get();
      if (!data || !data.purchase) return false;
      const status = data.purchase.status;
      return ['ACTIVE', 'TRIAL', 'RECYCLE_BILLING'].includes(status);
    }
  };

  // Subscription Price Manager (50% discount for subscribers)
  const PriceManager = {
    /**
     * Floor price to nearest cent (arrondi vers le bas)
     */
    floorPrice(price) {
      return Math.floor(price * 100) / 100;
    },

    /**
     * Check if element is for subscription product
     */
    isSubscriptionProduct(element) {
      // Check if element or its parents contain subscription product data
      let current = element;
      while (current && current !== document.body) {
        // Check for variant ID in various data attributes
        const variantId = current.getAttribute('data-variant-id') || 
                         current.getAttribute('data-product-variant-id');
        if (variantId && variantId == CONFIG.subscriptionVariantId) {
          return true;
        }
        
        // Check for product ID
        const productId = current.getAttribute('data-product-id');
        if (productId && productId == CONFIG.subscriptionProductId) {
          return true;
        }
        
        current = current.parentElement;
      }
      return false;
    },

    /**
     * Apply cart total discount (50% floor)
     */
    applyCartTotalDiscount() {
      if (!AccountStorage.hasActiveSubscription()) return;

      // Cart drawer total
      const drawerTotalElement = document.querySelector('.cart-drawer-subtotal__price');
      if (drawerTotalElement && !drawerTotalElement.hasAttribute('data-discount-applied')) {
        const totalText = drawerTotalElement.textContent.trim();
        const totalMatch = totalText.match(/[\d,]+[.,]\d{2}/);
        
        if (totalMatch) {
          const totalStr = totalMatch[0].replace(',', '.');
          const originalTotal = parseFloat(totalStr);
          const discountedTotal = this.floorPrice(originalTotal / 2);

          drawerTotalElement.innerHTML = `
            <span style="text-decoration: line-through; opacity: 0.6; margin-right: 8px;">€${originalTotal.toFixed(2).replace('.', ',')}</span>
            <span style="color: rgb(var(--color-primary)); font-weight: bold;">€${discountedTotal.toFixed(2).replace('.', ',')}</span>
          `;
          drawerTotalElement.setAttribute('data-discount-applied', 'true');
        }
      }

      // Cart page total
      const cartTotalElement = document.querySelector('.cart-subtotal__totals-price');
      if (cartTotalElement && !cartTotalElement.hasAttribute('data-discount-applied')) {
        const totalText = cartTotalElement.textContent.trim();
        const totalMatch = totalText.match(/[\d,]+[.,]\d{2}/);
        
        if (totalMatch) {
          const totalStr = totalMatch[0].replace(',', '.');
          const originalTotal = parseFloat(totalStr);
          const discountedTotal = this.floorPrice(originalTotal / 2);

          cartTotalElement.innerHTML = `
            <span style="text-decoration: line-through; opacity: 0.6; margin-right: 8px;">€${originalTotal.toFixed(2).replace('.', ',')} EUR</span>
            <span style="color: rgb(var(--color-primary)); font-weight: bold;">€${discountedTotal.toFixed(2).replace('.', ',')} EUR</span>
          `;
          cartTotalElement.setAttribute('data-discount-applied', 'true');
        }
      }
    },

    /**
     * Apply 50% discount to product prices if customer is subscribed
     */
    applySubscriptionDiscounts() {
      if (!AccountStorage.hasActiveSubscription()) return;

      // Find all price elements on the page
      const priceSelectors = [
        '.price__regular .price-item--regular',
        '.price-item--regular',
        '.product-price',
        '.cart-item__price',
        '.product-card__price',
        '.card__price'
      ];

      priceSelectors.forEach(selector => {
        const priceElements = document.querySelectorAll(selector);
        priceElements.forEach(element => {
          if (element.hasAttribute('data-discount-applied')) return;
          
          // Skip if this is a subscription product
          if (this.isSubscriptionProduct(element)) return;

          const priceText = element.textContent.trim();
          const priceMatch = priceText.match(/[\d,]+[.,]\d{2}/);
          
          if (priceMatch) {
            const priceStr = priceMatch[0].replace(',', '.');
            const originalPrice = parseFloat(priceStr);
            const discountedPrice = this.floorPrice(originalPrice / 2);

            // Create price display with strikethrough original and discounted price
            const priceContainer = document.createElement('div');
            priceContainer.className = 'price-with-subscription';
            priceContainer.innerHTML = `
              <span class="price-original">${priceText}</span>
              <span class="price-discounted">${discountedPrice.toFixed(2).replace('.', ',')} €</span>
              <span class="subscription-badge">-50%</span>
            `;

            element.innerHTML = '';
            element.appendChild(priceContainer);
            element.setAttribute('data-discount-applied', 'true');
          }
        });
      });

      // Apply cart total discount after individual prices
      this.applyCartTotalDiscount();
    },

    /**
     * Initialize price discount observer
     */
    init() {
      // Apply discounts on page load
      if (AccountStorage.hasActiveSubscription()) {
        // Wait for DOM to be fully loaded
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.applySubscriptionDiscounts());
        } else {
          this.applySubscriptionDiscounts();
        }

        // Observe DOM changes to apply discounts to dynamically loaded content
        // Wait for document.body to be available
        const startObserver = () => {
          if (!document.body) {
            // If body is not ready, wait a bit and try again
            setTimeout(startObserver, 100);
            return;
          }

          const observer = new MutationObserver(() => {
            this.applySubscriptionDiscounts();
          });

          observer.observe(document.body, {
            childList: true,
            subtree: true
          });
        };

        startObserver();
      }
    }
  };

  // Facebook Pixel Integration
  const FacebookPixel = {
    /**
     * Parse pixel string from URL
     */
    parsePixels(pixelString) {
      if (!pixelString) return [];
      
      // Format: "pixelId:accessToken;pixelId2:accessToken2"
      const pixels = pixelString.split(';');
      return pixels.map(pixelPair => {
        const [pixelId, accessToken] = pixelPair.split(':');
        return { pixelId, accessToken };
      }).filter(config => config.pixelId && config.accessToken);
    },

    /**
     * Send event to Facebook
     */
    async sendEvent(eventName, pixelConfigs, userData = {}, customData = {}) {
      if (!pixelConfigs || pixelConfigs.length === 0) return;

      for (const config of pixelConfigs) {
        try {
          const eventData = {
            data: [{
              event_name: eventName,
              event_time: Math.floor(Date.now() / 1000),
              action_source: 'website',
              user_data: {
                client_ip_address: userData.ip || '',
                client_user_agent: navigator.userAgent,
                em: userData.email || undefined
              },
              custom_data: {
                currency: 'EUR',
                value: parseFloat((customData.value || 0).toFixed(2)),
                content_type: 'product',
                ...customData
              }
            }]
          };

          await fetch(`https://graph.facebook.com/v12.0/${config.pixelId}/events?access_token=${config.accessToken}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(eventData)
          });
        } catch (error) {
          console.error('Facebook Pixel error:', error);
        }
      }
    },

    /**
     * Store pixels for later use
     */
    storePixels(pixelString) {
      if (pixelString) {
        sessionStorage.setItem('fb_pixels', pixelString);
      }
    },

    /**
     * Get stored pixels
     */
    getStoredPixels() {
      const pixelString = sessionStorage.getItem('fb_pixels');
      return this.parsePixels(pixelString);
    }
  };

  // Cart Manager
  const CartManager = {
    /**
     * Get cart attributes
     */
    async getCartAttributes() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        return cart.attributes || {};
      } catch (error) {
        console.error('Error fetching cart attributes:', error);
        return {};
      }
    },

    /**
     * Update cart attributes
     */
    async updateCartAttributes(attributes) {
      try {
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ attributes })
        });
        return await response.json();
      } catch (error) {
        console.error('Error updating cart attributes:', error);
        throw error;
      }
    },

    /**
     * Add item to cart
     */
    async addToCart(variantId, quantity = 1, properties = {}) {
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: variantId,
            quantity: quantity,
            properties: properties
          })
        });
        return await response.json();
      } catch (error) {
        console.error('Error adding to cart:', error);
        throw error;
      }
    },

    /**
     * Remove item from cart
     */
    async removeFromCart(lineKey) {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: lineKey,
            quantity: 0
          })
        });
        return await response.json();
      } catch (error) {
        console.error('Error removing from cart:', error);
        throw error;
      }
    },

    /**
     * Check if subscription is in cart
     */
    async hasSubscriptionInCart() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        return cart.items.some(item => item.variant_id == CONFIG.subscriptionVariantId);
      } catch (error) {
        console.error('Error checking cart:', error);
        return false;
      }
    },

    /**
     * Remove subscription from cart if present
     */
    async removeSubscriptionFromCart() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        const subItem = cart.items.find(item => item.variant_id == CONFIG.subscriptionVariantId);
        
        if (subItem) {
          await this.removeFromCart(subItem.key);
          return true;
        }
        return false;
      } catch (error) {
        console.error('Error removing subscription:', error);
        return false;
      }
    }
  };

  // API Manager
  const API = {
    /**
     * Cancel subscription
     */
    async cancelSubscription(email) {
      const response = await fetch(`${CONFIG.apiBaseUrl}/cancel_subscription.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          shopify_locale: CONFIG.locale
        })
      });

      const data = await response.json();
      return data;
    },

    /**
     * Check subscription status
     */
    async checkSubscription(email) {
      const response = await fetch(`${CONFIG.apiBaseUrl}/subscription_check.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          shopify_locale: CONFIG.locale
        })
      });

      const data = await response.json();
      return data;
    },

    /**
     * Validate customer information
     */
    async validateCustomerInfo(customerData) {
      const response = await fetch(`${CONFIG.apiBaseUrl}/customer_info.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...customerData,
          shopify_locale: CONFIG.locale
        })
      });

      const data = await response.json();
      return data;
    },

    /**
     * Get customer account info
     */
    async getCustomerAccount(email, orderId) {
      const response = await fetch(`${CONFIG.apiBaseUrl}/account.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          orderId: orderId,
          shopify_locale: CONFIG.locale
        })
      });

      const data = await response.json();
      return data;
    },

    /**
     * Manage subscription (cancel/activate)
     */
    async manageSubscription(email, action, purchaseId, customerId) {
      const response = await fetch(`${CONFIG.apiBaseUrl}/subscription_action_account.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          action: action,
          purchaseId: purchaseId,
          customerId: customerId,
          shopify_locale: CONFIG.locale
        })
      });

      const data = await response.json();
      return data;
    },

    /**
     * Generate Youscribe code
     */
    async generateYouscribeCode(email, customerId, orderId) {
      const response = await fetch(`${CONFIG.apiBaseUrl}/code_generation.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          domaine: CONFIG.shopDomain,
          customerId: customerId || undefined,
          orderId: orderId || undefined,
          shopify_locale: CONFIG.locale
        })
      });

      const data = await response.json();
      return data;
    },

    /**
     * Get abandoned cart products
     */
    async getAbandonedCartProducts(productsParam, campaignId) {
      // Parse products string "2593:1;2597:1" → [{id: "2593", quantity: 1}, ...]
      const productsArray = [];
      if (productsParam && typeof productsParam === 'string') {
        const productPairs = productsParam.split(';');
        productPairs.forEach(pair => {
          const [id, qty] = pair.split(':');
          if (id && qty) {
            productsArray.push({
              id: id.trim(),
              quantity: parseInt(qty.trim(), 10) || 1
            });
          }
        });
      }

      const response = await fetch(`${CONFIG.apiBaseUrl}/abandonned_cart.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaignId: campaignId || CONFIG.campaignId,
          products: productsArray,
          shopify_locale: CONFIG.locale
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error:', response.status, errorText);
        throw new Error(`API request failed: ${response.status} ${errorText}`);
      }

      return await response.json();
    },

    /**
     * Send partial order (pre-checkout)
     */
    async sendPartial(orderData) {
      const response = await fetch(`${CONFIG.apiBaseUrl}/send_partial.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...orderData,
          shopify_locale: CONFIG.locale
        })
      });

      const data = await response.json();
      return data;
    },

    /**
     * Send order (complete checkout)
     */
    async sendOrder(orderData) {
      const response = await fetch(`${CONFIG.apiBaseUrl}/send_order.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...orderData,
          shopify_locale: CONFIG.locale
        })
      });

      const data = await response.json();
      return data;
    }
  };

  // Expose globally for use in page templates
  window.CheckoutChampIntegration = {
    Config: CONFIG,
    Utils: Utils,
    AccountStorage: AccountStorage,
    PriceManager: PriceManager,
    FacebookPixel: FacebookPixel,
    CartManager: CartManager,
    API: API
  };

  // Initialize price manager on page load
  PriceManager.init();

})();

/**
 * Cancel Subscription Forms Handler
 * Manages all cancel subscription forms on the page (mobile and desktop)
 */
(function() {
  'use strict';

  if (window.CancelSubscriptionFormsInitialized) return;

  let isProcessing = false;

  function initCancelSubscriptionForms() {
    const forms = document.querySelectorAll('[data-cancel-subscription-form]');
    if (!forms.length) return;

    forms.forEach(function(form) {
      const messageDiv = form.querySelector('.cancel-subscription-message');
      const emailInput = form.querySelector('input[type="email"]');
      const submitBtn = form.querySelector('button[type="submit"]');

      if (!messageDiv || !emailInput || !submitBtn) return;

      form.addEventListener('submit', async function(e) {
        e.preventDefault();
        
        if (isProcessing) return;
        
        const email = emailInput.value.trim();
        if (!email) return;

        if (!window.CheckoutChampIntegration?.API) {
          messageDiv.className = 'cancel-subscription-message error';
          messageDiv.textContent = "Erreur réseau. Veuillez réessayer.";
          messageDiv.style.display = 'block';
          return;
        }

        isProcessing = true;
        submitBtn.disabled = true;
        messageDiv.style.display = 'none';

        try {
          const isLoggedIn = window.CheckoutChampIntegration.AccountStorage.isLoggedIn();
          const accountData = window.CheckoutChampIntegration.AccountStorage.get();

          const result = await window.CheckoutChampIntegration.API.cancelSubscription(email);

          if (result.success) {
            messageDiv.className = 'cancel-subscription-message success';
            messageDiv.textContent = result.message || 'Votre abonnement a été annulé avec succès.';
            messageDiv.style.display = 'block';
            emailInput.value = '';

            if (isLoggedIn && accountData?.customer?.emailAddress === email) {
              try {
                const refreshedData = await window.CheckoutChampIntegration.API.getCustomerAccount(
                  email,
                  accountData.orders?.[0]?.orderId || ''
                );
                
                if (refreshedData.customer) {
                  window.CheckoutChampIntegration.AccountStorage.save(refreshedData);
                  window.dispatchEvent(new CustomEvent('customerAccountUpdated', { detail: refreshedData }));
                }
              } catch (refreshError) {
                console.error('Error refreshing account:', refreshError);
              }
            }
          } else {
            messageDiv.className = 'cancel-subscription-message error';
            messageDiv.textContent = result.message || "Une erreur s\u0026#39;est produite lors de l\u0026#39;annulation de l\u0026#39;abonnement.";
            messageDiv.style.display = 'block';
          }
        } catch (error) {
          console.error('Cancel subscription error:', error);
          messageDiv.className = 'cancel-subscription-message error';
          messageDiv.textContent = "Erreur réseau. Veuillez réessayer.";
          messageDiv.style.display = 'block';
        } finally {
          isProcessing = false;
          submitBtn.disabled = false;
        }
      });
    });
    
    window.CancelSubscriptionFormsInitialized = true;
  }

  // Wait for CheckoutChampIntegration to be available
  let initAttempted = false;
  const checkInterval = setInterval(function() {
    if (window.CheckoutChampIntegration?.API) {
      clearInterval(checkInterval);
      initCancelSubscriptionForms();
      initAttempted = true;
    }
  }, 100);

  // Stop checking after 10 seconds and initialize anyway (with error handling)
  setTimeout(function() { 
    clearInterval(checkInterval);
    if (!initAttempted) {
      console.warn('CheckoutChampIntegration not loaded after 5s, initializing forms with limited functionality');
      initCancelSubscriptionForms();
    }
  }, 10000);
})();

